import { useQuery, type QueryStatus } from "@tanstack/react-query";
import { BarChart3 } from "lucide-react";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard, LockedContent } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandIntelApiFactory } from "@/apis/brand-os/brand-intel";

interface FormatMixItem {
  label: string;
  count: number;
  percentage: number;
}

function FormatMixBars({ items }: { items: FormatMixItem[] }) {
  if (!items.length) return <LockedContent message={message} className="flex-1 min-h-0" />;
  return (
    <Card className="flex flex-col flex-1 min-w-0">
      <CardHeader className="flex flex-row items-center gap-2">
        <div className="rounded-md bg-success/10 p-2">
          <BarChart3 className="size-5 text-success" strokeWidth={2.5} />
        </div>
        <CardTitle className="text-base font-semibold">Format Mix vs Performance</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {items.map((item) => (
          <div key={item.label} className="flex items-center gap-3">
            <p className="w-44 shrink-0 text-sm text-foreground">{item.label}</p>
            <div className="relative min-w-0 flex-1">
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-success transition-all" style={{ width: `${item.percentage}%` }} />
              </div>
            </div>
            <p className="w-8 shrink-0 text-right text-sm font-semibold text-foreground">{item.count}</p>
            <p className="w-10 shrink-0 text-right text-sm text-muted-foreground">{item.percentage}%</p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function FormatMixContent({ status, items }: { status: QueryStatus; items: FormatMixItem[] }) {
  switch (status) {
    case "pending":
      return <FormatMixSkeleton />;
    case "error":
      return <LockedCard message={message} className="flex-1 min-h-0" />;
    case "success":
      return <FormatMixBars items={items} />;
  }
}

export function FormatMixPerformance() {
  const { data, status } = useQuery(BrandIntelApiFactory.Queries.FetchCreativeAudit());

  const items = (data?.media_type_distribution ?? [])
    .filter((item) => item.count > 0 && item.percentage > 0 && item.media_type)
    .map((item) => ({ label: item.media_type, count: item.count, percentage: Math.round(item.percentage) }));

  return <FormatMixContent status={status} items={items} />;
}
function FormatMixSkeleton() {
  return (
    <Card className="flex flex-col flex-1 min-w-0">
      <CardHeader className="flex flex-row items-center gap-2">
        <Skeleton className="size-9 rounded-md" />
        <Skeleton className="h-5 w-40" />
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="h-4 w-44 shrink-0" />
            <Skeleton className="h-2 flex-1 rounded-full" />
            <Skeleton className="h-4 w-8 shrink-0" />
            <Skeleton className="h-4 w-10 shrink-0" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
